import React, { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { SplitButton } from "@/components/ui/split-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList
} from "@/components/ui/command";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Redo } from "@dreamhub/designsystem/icons";
import { DealStage } from "@/pages/deals/types";
import { find, isEmpty } from "lodash";

interface Props {
  stages?: DealStage[];
  onStageSelection: (value: number) => void;
  currentStageId?: number;
  className?: string;
  isClosed?: boolean;
  isCurrentStageClosed?: boolean;
}

export const StageSelection: React.FC<Props> = ({
  stages = [],
  onStageSelection,
  currentStageId,
  className,
  isClosed
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { t } = useTranslation();

  const currentStage = find(stages, (stage) => stage.id === currentStageId);
  const currentStageOrder = currentStage?.stageOrder || 1;

  const nextStage =
    find(stages, (stage) => stage.stageOrder === currentStageOrder + 1) || null;

  const isSecondToLastStage =
    nextStage &&
    !find(stages, (stage) => stage.stageOrder === currentStageOrder + 2);

  // Filter stages for dropdown - exclude the last stage if we're at second-to-last
  const dropdownStages = isSecondToLastStage ? stages.slice(0, -1) : stages;

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);
  };

  const handleMainButtonClick = () => {
    if (!isEmpty(nextStage)) {
      onStageSelection(nextStage.id);
    }
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={handleOpenChange}>
      <SplitButton
        className={cn("h-8", className)}
        mainContent={
          !isClosed ? (
            <div className="flex items-center gap-1">
              <IconContainer size="5">
                <Redo />
              </IconContainer>
              <Text variant="label">
                {isEmpty(nextStage)
                  ? currentStage?.name || t("deals.dealView.closed")
                  : `${t("deals.dealView.proceed")} ${nextStage.name}`}
              </Text>
            </div>
          ) : undefined
        }
        onMainClick={handleMainButtonClick}
        onSplitClick={() => setIsPopoverOpen(!isPopoverOpen)}
        splitButtonAsChild={true}
        splitContent={
          <PopoverTrigger asChild>
            {isClosed ? (
              <Text variant="label" className="px-2">
                {t("deals.dealView.closeDeal")}
              </Text>
            ) : (
              <ChevronDown
                className={cn(
                  "h-5 cursor-pointer text-muted-foreground transition-transform duration-200",
                  isPopoverOpen ? "rotate-180" : "rotate-0"
                )}
              />
            )}
          </PopoverTrigger>
        }
        isOpen={isPopoverOpen}
      />
      <PopoverContent
        padding={4}
        className="flex flex-col gap-2 w-fit"
        align="end"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
        sideOffset={4}
      >
        <Command>
          <CommandList>
            <CommandGroup>
              {dropdownStages.map((stage) => {
                const isCurrent = stage.id === currentStageId;
                return (
                  <CommandItem
                    key={stage.id}
                    onSelect={() => {
                      if (!isCurrent) {
                        setIsPopoverOpen((prev) => !prev);
                        return onStageSelection(stage.id);
                      }
                      return null;
                    }}
                    className={cn(
                      "w-full cursor-pointer py-2 pl-2 pr-3 min-h-9 flex justify-between mr-3 rounded-lg transition-all duration-200 ease-in-out",
                      isCurrent
                        ? "font-semibold cursor-not-allowed !bg-transparent hover:!bg-transparent"
                        : "hover:!bg-surface-hover-soft active:!bg-surface-pressed"
                    )}
                  >
                    <Text variant={isCurrent ? "label" : "body"}>
                      {stage.name}
                    </Text>
                    {isCurrent && (
                      <IconContainer size="5">
                        <Check />
                      </IconContainer>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default StageSelection;
